import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useGetSimulationsQuery, useDeleteSimulationMutation, useUploadSimulationCsvMutation, useUploadToS3Mutation } from "../../store/services/simulation-service";
import { useGetFeatureSwitchesQuery } from "../../store/services/tenancy.service";
import { useSelector } from "react-redux";
import {
    Alert,
    Autocomplete,
    Button,
    IconButton,
    Modal,
    Dialog,
    DialogContent,
    Snackbar,
    Box,
    useMediaQuery,
    CircularProgress
} from "@mui/material";
import SimulationPreview from "../on-demand/SimulationPreview";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import CloseIcon from '@mui/icons-material/Close';
import Rating from "@mui/material/Rating";
import Table from "../../components/Table/Table";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from "react-router-dom";
import UsersTable from "../users/UsersTable";
import AddSimulationModal from "../on-demand/CreateOnDemand/AddSimulationModal";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import { useTheme } from "@mui/material/styles";
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ShuffleIcon from "@mui/icons-material/Shuffle";
import TrimmedTextField from "../../components/TrimmedTextField";
import ConfirmationDialog from "../../utils/confirmationDialog";
import SharingDisplay from '../../components/ContentSharing/SharingDisplay';

const userModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    boxShadow: 24,
    borderRadius: 4,
    bgcolor: "background.paper"
};

SimulationTable.propTypes = {
    type: PropTypes.oneOf(["edit", "select", "library"]).isRequired,
    simulationIds: PropTypes.array,
    setSimulationId: PropTypes.func,
    onRandomSelect: PropTypes.func
};

export default function SimulationTable(props) {
    const { type, simulationIds, setSimulationId, onRandomSelect } = props;
    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { tenancyId } = useSelector((state) => state.tenancy);

    // Updated query params logic for library mode
    let additionalQueryParams = "";
    if (type === "edit") {
        additionalQueryParams = "";
    } else if (type === "select" || type === "library") {
        additionalQueryParams = `&published=true&tenancyId=${tenancyId}`;
    }

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    const { data: simulationData, isLoading: simulationsLoading, error: simulationsError } = useGetSimulationsQuery({
        perPage: paginationModel.pageSize,
        page: paginationModel.page,
        includes: "",
        query: "",
        additionalQueryParams: additionalQueryParams
    });

    // Get feature switches for current tenancy
    const {
        data: featureSwitchesApiData,
        isLoading: featureSwitchesLoading
    } = useGetFeatureSwitchesQuery({ tenancyId });

    // Extract actual feature data
    const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;

    const [uploadToS3] = useUploadToS3Mutation();
    const [uploadSimulationCsv] = useUploadSimulationCsvMutation();
    const [deleteSimulation] = useDeleteSimulationMutation();

    const [selectedSimulation, setSelectedSimulation] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [simulationTypes, setSimulationTypes] = useState([]);
    const [simulationType, setSimulationType] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const fileInputRef = useRef(null);
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isTenancyAdmin, setIsTenancyAdmin] = useState(false);
    const [isUser, setIsUser] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [addSimulationModal, setAddSimulationModal] = useState(false);

    // Confirmation Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogContent, setDialogContent] = useState("");
    const [dialogAction, setDialogAction] = useState(null);

    // state for tenancy filter (library mode only)
    const [tenancyFilter, setTenancyFilter] = useState("My Simulations");

    const tenancyFilterOptions = [
        "My Simulations",
        "Other Simulations",
        "All Simulations"
    ];

    useEffect(() => {
        if (simulationData) {
            const types = simulationData.map((simulation) => simulation.simulationType);
            const uniqueTypes = [...new Set(types)];
            setSimulationTypes(uniqueTypes);
        }
    }, [simulationData]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setIsAdmin(user && user.role === "Administrators");
        setIsTenancyAdmin(user && user?.role?.includes("TenancyAdministrators"))
        setIsUser(user && user.role === "");
    }, []);

    // Filtering function - replaces useMemo logic
    const getFilteredSimulations = () => {
        if (!simulationData) {
            return [];
        }

        let filtered = simulationData;

        // Apply simulation type filter
        if (simulationType) {
            filtered = filtered.filter((simulation) => simulation.simulationType === simulationType);
        }

        // Apply tenancy filter for library mode
        if (type === "library") {
            if (tenancyFilter === "My Simulations") {
                filtered = filtered.filter(simulation =>
                    simulation.sharedTenancyIds && simulation.sharedTenancyIds.includes(Number(tenancyId))
                );
            } else if (tenancyFilter === "Other Simulations") {
                filtered = filtered.filter(simulation =>
                    !simulation.sharedTenancyIds || !simulation.sharedTenancyIds.includes(Number(tenancyId))
                );
            }
            // "All Simulations" doesn't need additional filtering
        }

        return filtered;
    };

    const handleSimulationTypeChange = (event, value) => {
        setSimulationType(value);
    };

    const handleTenancyFilterChange = (event, value) => {
        setTenancyFilter(value || "My Simulations");
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            handleSendSimUsingCSV(file);
        }
    };

    const handleDeleteSimulation = async (id) => {
        setDialogTitle("Delete Simulation");
        setDialogContent("Are you sure you want to delete the selected simulation? This action is irreversible.");
        setDialogAction(() => async () => {
            try {
                setDeletingId(id);
                const response = await deleteSimulation(id).unwrap();
                setSnackbarSeverity("success");
                setSnackbarMessage("Simulation deleted successfully!");
                setSnackbarOpen(true);
            } catch (error) {
                console.error('Error deleting simulation:', error);
                setSnackbarSeverity("error");
                setSnackbarMessage("Failed to delete simulation");
                setSnackbarOpen(true);
            } finally {
                setDeletingId(null);
            }
        });
        setDialogOpen(true);
    };

    const handleSendSimUsingCSV = async (file) => {
        if (file.type === "application/vnd.ms-excel" || file.type === "text/csv") {
            try {
                const response = await uploadSimulationCsv({ file }).unwrap();
                setSnackbarMessage(response.success ?
                    "Upload successful" :
                    "Error uploading CSV");
                setSnackbarSeverity(response.success ? "success" : "error");
                setSnackbarOpen(true);
            } catch (error) {
                console.error("Error uploading CSV:", error);
                setSnackbarMessage(error.data || "Error uploading CSV");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } else {
            setSnackbarMessage("Invalid file type. Please upload a CSV file.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
    };

    const handleAddSimulation = () => {
        if (type === "edit" || type === "library") {
            navigate("/simulations/add");
        } else {
            setAddSimulationModal(true);
        }
    };

    const handleCopySimulation = (simulation) => {
        navigate(`/simulations/add?copyFrom=${simulation.id}`);
    };

    const handleViewSimulation = (simulation) => {
        navigate(`/simulations/${simulation.id}`);
    };

    const handleEditSimulation = (simulation) => {
        navigate(`/simulations/edit/${simulation.id}`);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const handleSelectSimulation = (simulationId) => {
        setSelected(simulationId);
    };

    const handlePreview = (row) => {
        setSelectedSimulation(row);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedSimulation(null);
    };

    const handleCloseUserModal = () => {
        setSelected([]);
        setUserModalOpen(false);
    };

    const handleOpenUserModal = (simulationId) => {
        setSelected(simulationId);
        setUserModalOpen(true);
    };

    const handleRandomSelection = () => {
        if (onRandomSelect) {
            onRandomSelect();
        }
    };

    const handleBrowseAllSimulations = () => {
        setTenancyFilter("All Simulations");
    };

    const ResponsiveSendUserButton = ({ dataRow }) => (
        <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => handleOpenUserModal(dataRow.id)}
            sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                minWidth: 0,
            }}
        >
            {isMobile ? "Send" : "Send to User"}
        </Button>
    );

    // Check if we should show library empty state
    const showLibraryEmptyState = type === "library" &&
        tenancyFilter === "My Simulations" &&
        getFilteredSimulations().length === 0 &&
        !simulationsLoading &&
        !simulationsError;

    const toolbarActions = [
        {
            render: () => (
                <Box width={"100%"} justifyContent={"flex-end"} display={"flex"} alignItems={"center"}>
                    {/* Tenancy Filter for Library Mode */}
                    {type === "library" && (
                        <Autocomplete
                            id="tenancy-filter-autocomplete"
                            disableClearable
                            sx={{ mr: 2, width: 180 }}
                            size="small"
                            value={tenancyFilter}
                            options={tenancyFilterOptions}
                            onChange={handleTenancyFilterChange}
                            renderInput={(params) =>
                                <TrimmedTextField {...params}
                                    style={{ backgroundColor: 'background.default' }}
                                    label="Sharing"
                                />
                            }
                        />
                    )}

                    <Autocomplete
                        id="simulation-type-autocomplete"
                        disableClearable={!simulationType}
                        sx={{ mr: 2, width: 120 }}
                        size="small"
                        multiple={false}
                        value={simulationType || "All"}
                        options={simulationTypes}
                        onChange={handleSimulationTypeChange}
                        renderInput={(params) => <TrimmedTextField {...params} style={{ backgroundColor: 'background.default' }} label="Type" />}
                    />

                    {type === "select" && actualData?.enableRandomSimulation && (
                        <Button
                            size="medium"
                            variant="contained"
                            color="warning"
                            sx={{ textTransform: "capitalize", mr: 2 }}
                            onClick={handleRandomSelection}
                            endIcon={<ShuffleIcon />}
                        >
                            Random
                        </Button>
                    )}

                    {!isUser && actualData?.enableAddSimulation && <Button
                        size="medium"
                        variant="contained"
                        sx={{ textTransform: "capitalize" }}
                        onClick={handleAddSimulation}
                        endIcon={<AddIcon />}
                    >
                        Add Sim
                    </Button>}
                    {type == "edit" && isAdmin && (
                        <>
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                onChange={handleFileChange}
                            />
                            <Button
                                size="medium"
                                variant="contained"
                                color="secondary"
                                sx={{ bgcolor: (theme) => theme.palette.secondary.lighter, ml: 2, }}
                                onClick={() => fileInputRef.current.click()}
                            >
                                send using csv
                            </Button>
                        </>
                    )}
                </Box>
            )
        }
    ];

    let headCells = [
        {
            id: "select",
            label: "Select",
            render: (dataRow) => {
                const isSelected = simulationIds?.map(sim => sim.id).includes(dataRow.id);
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (isSelected) {
                                setSimulationId(simulationIds?.filter(sim => sim.id !== dataRow.id));
                            } else {
                                setSimulationId([...simulationIds, dataRow]);
                            }
                        }}
                    >
                        {isSelected ? <CheckBoxRoundedIcon color="warning" /> : <CheckBoxOutlineBlankRoundedIcon color={lightMode ? "black" : "white"} />}
                    </IconButton>
                );
            },
            initialVisible: type === "select"
        },
        {
            id: "preview",
            label: "Preview",
            render: (dataRow) => (
                <>
                    <Button onClick={() => handlePreview(dataRow)} size="small" variant="contained" color="warning">
                        Preview
                    </Button>
                </>
            ),
            initialVisible: true
        },
        {
            id: "edit",
            label: "Edit",
            render: (dataRow) => {
                // Updated edit visibility logic for library mode
                let isVisible = false;
                if (type === "edit") {
                    // Original logic for edit mode (super admin)
                    isVisible = isAdmin || (isTenancyAdmin && dataRow.sharedTenancyIds.includes(Number(tenancyId)));
                } else if (type === "library") {
                    // Library mode: only show edit for user's own tenancy simulations
                    isVisible = isAdmin || (dataRow.sharedTenancyIds && dataRow.sharedTenancyIds.includes(Number(tenancyId)));
                }

                return (
                    <Button
                        onClick={() => handleEditSimulation(dataRow)}
                        size="small"
                        variant="contained"
                        color="secondary"
                        sx={{ display: isVisible ? "block" : "none" }}
                    >
                        Edit
                    </Button>
                );
            },
            initialVisible: true
        },
        {
            id: "copy",
            label: "Copy",
            render: (dataRow) => (
                <Button
                    onClick={() => handleCopySimulation(dataRow)}
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{
                        whiteSpace: 'nowrap',
                        minWidth: 0
                    }}
                    endIcon={< AddIcon />}
                >
                    duplicate
                </Button>
            ),
            initialVisible: type !== "select"
        },
        {
            id: "send-user",
            label: "Send",
            render: (dataRow) => <ResponsiveSendUserButton dataRow={dataRow} />,
            initialVisible: false
        },
        {
            id: "simulationName",
            label: "Simulation Name",
            initialVisible: true
        },
        {
            id: "sharing",
            label: "Sharing",
            render: (dataRow) => (
                <SharingDisplay
                    published={dataRow.published}
                    sharedTenancyIds={dataRow.sharedTenancyIds}
                    publishToFamily={dataRow.publishToFamily}
                    variant="compact"
                />
            ),
            initialVisible: type === "edit"
        },
        {
            id: "emailFromAddress",
            label: "Email From Address",
            initialVisible: type === "select"
        },
        {
            id: "emailDisplayName",
            label: "Email Display Name",
            initialVisible: false
        },
        {
            id: "emailSubject",
            label: "Email Subject",
            initialVisible: false
        },
        {
            id: "simulationType",
            label: "Type",
            initialVisible: true
        },
        {
            id: "difficultyRating",
            label: "Difficulty",
            render: (dataRow) => (
                <Rating
                    name="difficulty-rating"
                    value={dataRow.difficultyRating}
                    max={3}
                    readOnly
                    icon={<StarRoundedIcon fontSize="inherit" />}
                    emptyIcon={<StarRoundedIcon fontSize="inherit" />}
                />
            ),
            initialVisible: type !== "edit"
        },
        {
            id: "linkedUniversityCourseId",
            label: "Linked Courses",
            initialVisible: type === "edit"
        },
        {
            id: "action",
            label: "Delete",
            initialVisible: type === "edit",
            render: (dataRow) => {
                if (!isAdmin) return <></>;

                return (
                    <Button
                        variant="contained"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteSimulation(dataRow.id)}
                        disabled={deletingId === dataRow.id}
                        sx={{
                            minWidth: '70px',
                            width: '70px',
                            height: '32px',
                            padding: '6px 8px',
                            '& .MuiCircularProgress-root': {
                                marginRight: 0
                            }
                        }}
                    >
                        {deletingId === dataRow.id ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            "Delete"
                        )}
                    </Button>
                );
            }

        }
    ];

    return (
        <>
            <Table
                name={"Simulation"}
                data={getFilteredSimulations()}
                error={simulationsError}
                loading={simulationsLoading}
                headCells={headCells}
                search={true}
                editColumn={type === "edit"}
                checkbox={false}
                toolbarActions={toolbarActions}
                showToolbarActions={true}
                onDelete={() => console.log("Delete Simulation Not Allowed")}
                showDelete={false}
                onCellClick={handleViewSimulation}
                selected={selected}
                setSelected={handleSelectSimulation}
                maxTableHeight={type === "select" ? "calc(100vh - 350px)" : "calc(100vh - 250px)"}
                initialVisibleColumns={headCells.filter(cell => cell.initialVisible).map(cell => cell.label)}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                emptyStateConfig={showLibraryEmptyState ? {
                    title: "No custom simulations found for your organization",
                    message: "Change the sharing filter to 'All Simulations' to browse our full library, or click 'Add Sim' to create a custom simulation.",
                    actions: [
                        {
                            label: "Browse All Simulations",
                            onClick: handleBrowseAllSimulations,
                            variant: "outlined"
                        },
                        ...(!isUser && actualData?.enableAddSimulation ? [{
                            label: "Add Sim",
                            onClick: handleAddSimulation,
                            variant: "contained",
                            icon: <AddIcon />
                        }] : [])
                    ]
                } : undefined}
            />

            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '50%',
                        height: '80%',
                        margin: '0 auto',
                        backgroundColor: theme.palette.background.default,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                    }
                }}
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100vh'
                    },
                    '& .MuiPaper-root': {
                        m: 0
                    }
                }}
            >
                {/* Close button */}
                <IconButton
                    onClick={handleCloseModal}
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: -45,
                        bgcolor: theme.palette.background.default,
                        boxShadow: 2,
                        '&:hover': {
                            bgcolor: theme.palette.background.default,
                            opacity: 0.9
                        },
                        width: 40,
                        height: 40,
                        zIndex: (theme) => theme.zIndex.modal + 1
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Dialog content - single scroll container */}
                <DialogContent
                    sx={{
                        p: 0,
                        height: '100%',
                        width: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                        backgroundColor: theme.palette.background.paper
                    }}
                >
                    {selectedSimulation && (
                        <SimulationPreview simulationId={selectedSimulation.id} />
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <Modal open={userModalOpen} onClose={handleCloseUserModal} aria-labelledby="user-selection-modal"
                aria-describedby="user-selection-description">
                <Box sx={userModalStyle}>
                    <UsersTable
                        type={"send"}
                        userId={selectedUserId}
                        setUserId={setSelectedUserId}
                        formatId={selected}
                        format={"simulation"}
                        CSVButton
                    />
                </Box>
            </Modal>

            <ConfirmationDialog
                open={dialogOpen}
                title={dialogTitle}
                content={dialogContent}
                onConfirm={() => {
                    setDialogOpen(false);
                    dialogAction();
                }}
                onCancel={() => setDialogOpen(false)}
            />
            <AddSimulationModal open={addSimulationModal} handleClose={() => setAddSimulationModal(false)} />
        </>
    );
}